import { useEffect, useState } from "react";
import { api } from "@service/api";
import { Card } from "@components/cards/card";
import { ZuToastifyStore } from "@hooks/stores/zustand-toastify-store";
import {
  FaExpand,
  FaMinus,
  FaEye,
  FaEyeSlash,
  FaMoneyBillWave,
  FaTag,
  FaDollarSign,
} from "react-icons/fa6";
import { IRaffleDefaultDTO } from "@hooks/dtos/raffles/IRaffleDefaultDTO";
import { LoadingSkeleton } from "@components/skeleton-loading";
import { getRaffleStatus } from "@hooks/functions/Raffle/status";
import { Masks } from "@hooks/functions/Masks/Masks";
import dayjs from "dayjs";
import { FaCalendarAlt, FaTicketAlt, FaUserAlt } from "react-icons/fa";
import { formatName } from "@hooks/functions/formatName";
import { Link } from "react-router-dom";

interface IProps {
  raffleKey: string;
  campaign?: string;
  title?: string;
  retRaffle?: (raffle: IRaffleDefaultDTO) => void;
}

interface IShow {
  campaign: boolean;
  description: boolean;
}

const masks = new Masks();
export const RafflePrimaryInfoVisitor = ({
  raffleKey,
  campaign,
  title,
  retRaffle,
}: IProps) => {
  const {
    actions: { pushToastify },
  } = ZuToastifyStore();

  const [show, setShow] = useState<IShow>({
    campaign: false,
    description: false,
  });

  const [raffle, setRaffle] = useState<IRaffleDefaultDTO>();

  const handleCampaignInfo = async () => {
    try {
      const params = `?key=${raffleKey}&campaign=${campaign}`;
      const result = await api.get(`/raffle/primaryInfo${params}`);
      setRaffle(result.data);
    } catch (err: any) {
      console.error(err);
      pushToastify({
        message: err?.message || "Erro ao encontrar informações da Campanha.",
        type: "error",
        theme: "dark",
        pauseOnHover: true,
      });
    }
  };

  useEffect(() => {
    if (raffle) {
      retRaffle?.(raffle);
    }
  }, [raffle]);

  useEffect(() => {
    handleCampaignInfo();
  }, []);

  const raffleStatus = raffle ? getRaffleStatus(raffle.status) : null;

  return (
    <Card className="">
      <div
        className="card-header bg-white border-0 py-3"
        style={{ cursor: "pointer" }}
        onClick={() => {
          setShow((prev) => ({
            ...prev,
            campaign: !show.campaign,
          }));
        }}
      >
        <div className="d-flex justify-content-between align-items-center">
          <div>
            <h5 className="card-title text-dark fw-bold mb-1">
              {title || ""}
              {raffle ? raffle.name : `Informações da Campanha`}
            </h5>
            <p className="card-subtitle text-muted small mb-0">
              {raffle ? raffle.description_short : `Resumo da Campanha`}
            </p>
          </div>
          <div className="d-flex align-items-center gap-2">
            {raffle && raffleStatus && (
              <span
                className={`badge bg-${raffleStatus.color} text-white fs-6 py-2 px-3`}
                title={raffleStatus.title}
              >
                {raffleStatus.text}
              </span>
            )}
            {show.campaign ? (
              <FaMinus
                className="fs-5"
                title="Clique para ocultar as informações da Campanha."
              />
            ) : (
              <FaExpand
                className="fs-5"
                title="Clique para visualizar as informações da Campanha."
              />
            )}
          </div>
        </div>
      </div>

      <div
        className={`card-body bg-light-subtle p-4 ${show.campaign || `d-none`}`}
      >
        {!raffle ? (
          <LoadingSkeleton height={200} count={1} />
        ) : (
          <>
            {/* Main stats - Compact horizontal layout */}
            <div className="row g-3 mb-4">
              {/* Owner Info */}
              <div className="col-md-4 col-6">
                <Link to={`/profile?atsign=${raffle.customerData.atSign}`}>
                  <div className="d-flex align-items-center bg-white rounded-3 p-3 border h-100">
                    <div className="bg-primary-subtle rounded-2 p-2 me-3">
                      <FaUserAlt className="text-primary fs-5" />
                    </div>
                    <div className="flex-grow-1">
                      <div className="d-flex justify-content-between align-items-center text-muted small mb-1">
                        <span>Proprietário(a)</span>
                        <span className="badge bg-secondary text-white fs-6 px-3">
                          @{raffle?.customerData?.atSign}
                        </span>
                      </div>
                      <div className="h5 fw-bold text-info mb-0">
                        {formatName(raffle?.customerData?.name || "")}
                      </div>
                    </div>
                  </div>
                </Link>
              </div>

              {/* Campaign Name */}
              <div className="col-md-4 col-6">
                <div className="d-flex align-items-center bg-white rounded-3 p-3 border h-100">
                  <div className="bg-primary-subtle rounded-2 p-2 me-3">
                    <FaTag className="text-primary fs-5" />
                  </div>
                  <div>
                    <div className="text-muted small mb-1">Campanha</div>
                    <div className="h5 fw-bold text-primary mb-0">
                      {raffle.name}
                    </div>
                  </div>
                </div>
              </div>

              {/* Ticket Type */}
              <div
                className="col-md-4 col-6"
                onClick={() => {
                  // if (raffle?.typeData?.id) {
                  //   handleModalRaffleTypeOpen(raffle?.typeData?.id);
                  // } else {
                  //   errorMessage(`Houve um erro ao carregar o tipo de Campanha.`);
                  // }
                }}
              >
                <div className="d-flex align-items-center bg-white rounded-3 p-3 border h-100">
                  <div className="bg-secondary-subtle rounded-2 p-2 me-3">
                    <FaTicketAlt className="text-secondary fs-5" />
                  </div>
                  <div>
                    <div className="text-muted small mb-1">Tipo de Ticket</div>
                    <div
                      className="h5 fw-bold text-dark mb-0"
                      title={
                        raffle?.typeData?.description_short ||
                        "Erro ao carregar informação."
                      }
                    >
                      {raffle?.typeData?.name}
                    </div>
                  </div>
                </div>
              </div>

              {/* Ticket Amount */}
              <div className="col-md-4 col-6">
                <div className="d-flex align-items-center bg-white rounded-3 p-3 border h-100">
                  <div className="bg-info-subtle rounded-2 p-2 me-3">
                    <FaTicketAlt className="text-info fs-5" />
                  </div>
                  <div>
                    <div className="text-muted small mb-1">
                      Total de Tickets
                    </div>
                    <div className="h5 fw-bold text-dark mb-0">
                      {masks.formatMilhar(raffle.quantity || 0)}
                    </div>
                  </div>
                </div>
              </div>

              {/* Ticket Price */}
              <div className="col-md-4 col-6">
                <div className="d-flex align-items-center bg-white rounded-3 p-3 border h-100">
                  <div className="bg-warning-subtle rounded-2 p-2 me-3">
                    <FaMoneyBillWave className="text-warning fs-5" />
                  </div>
                  <div>
                    <div className="text-muted small mb-1">Preço Unitário</div>
                    <div className="h5 fw-bold text-dark mb-0">
                      {masks.brlCurrency(raffle.price)}
                    </div>
                  </div>
                </div>
              </div>

              {/* Total Reward */}
              <div className="col-md-4 col-6">
                <div className="d-flex align-items-center bg-white rounded-3 p-3 border h-100">
                  <div className="bg-success-subtle rounded-2 p-2 me-3">
                    <FaDollarSign className="text-success fs-5" />
                  </div>
                  <div>
                    <div className="text-muted small mb-1">Premiação Total</div>
                    <div className="h5 fw-bold text-success mb-0">
                      {masks.brlCurrency(raffle.rewardPriceMarket)}
                    </div>
                  </div>
                </div>
              </div>

              {/* Slug */}
              <div className="col-md-4 col-6">
                <div className="d-flex align-items-center bg-white rounded-3 p-3 border h-100">
                  <div className="bg-secondary-subtle rounded-2 p-2 me-3">
                    <FaDollarSign className="fs-5" />
                  </div>
                  <div>
                    <div className="text-muted small mb-1">
                      Slug da Campanha
                    </div>
                    <div className="h5 fw-bold mb-0">#{raffle.campaign}</div>
                  </div>
                </div>
              </div>
            </div>

            {/* Dates section - Compact timeline */}
            <div className="row g-3 mb-4">
              <div className="col-12">
                <div className="bg-white rounded-3 border p-3">
                  <div className="d-flex align-items-center mb-2">
                    <FaCalendarAlt className="text-muted me-2" />
                    <span className="text-muted small">Datas Importantes</span>
                  </div>
                  <div className="row g-3">
                    <div className="col-md-3">
                      <div className="border-start border-3 border-primary ps-3 py-1">
                        <div className="text-muted small">Criada em</div>
                        <div className="fw-bold">
                          {dayjs(raffle.createdAt).format("DD/MM/YYYY")}
                        </div>
                        <div className="text-muted small">
                          às {dayjs(raffle.createdAt).format("HH:mm:ss")}
                        </div>
                      </div>
                    </div>

                    {raffle.activationDate && (
                      <div className="col-md-3">
                        <div className="border-start border-3 border-success ps-3 py-1">
                          <div className="text-muted small">Ativada em</div>
                          <div className="fw-bold text-success">
                            {dayjs(raffle.activationDate).format("DD/MM/YYYY")}
                          </div>
                          <div className="text-muted small">
                            às {dayjs(raffle.activationDate).format("HH:mm:ss")}
                          </div>
                        </div>
                      </div>
                    )}

                    {raffle.autoActivationDate && (
                      <div className="col-md-3">
                        <div className="border-start border-3 border-info ps-3 py-1">
                          <div className="text-muted small">
                            Ativação Automática
                          </div>
                          <div className="fw-bold text-info">
                            {dayjs(raffle.autoActivationDate).format(
                              "DD/MM/YYYY",
                            )}
                          </div>
                          <div className="text-muted small">
                            às{" "}
                            {dayjs(raffle.autoActivationDate).format(
                              "HH:mm:ss",
                            )}
                          </div>
                        </div>
                      </div>
                    )}

                    {raffle.finishedAt && (
                      <div className="col-md-3">
                        <div className="border-start border-3 border-warning ps-3 py-1">
                          <div className="text-muted small">Finalizada em</div>
                          <div className="fw-bold text-info">
                            {dayjs(raffle.finishedAt).format("DD/MM/YYYY")}
                          </div>
                          <div className="text-muted small">
                            às {dayjs(raffle.finishedAt).format("HH:mm:ss")}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Description section */}
            <div
              className="bg-white rounded-3 border"
              style={{ cursor: "pointer" }}
              onClick={(e) => {
                e.stopPropagation();
                setShow((prev) => ({
                  ...prev,
                  description: !prev.description,
                }));
              }}
            >
              <div className="p-3 border-bottom">
                <div className="d-flex justify-content-between align-items-center">
                  <div className="d-flex align-items-center">
                    <FaEye className="text-muted me-2" />
                    <span className="text-muted small">Descrição Completa</span>
                  </div>
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-primary d-flex align-items-center"
                  >
                    {show.description ? (
                      <FaEyeSlash size={14} className="me-1" />
                    ) : (
                      <FaEye size={14} className="me-1" />
                    )}
                    {show.description ? "Ocultar" : "Mostrar"}
                  </button>
                </div>
              </div>

              {show.description && (
                <div className="p-3">
                  <div
                    className="text-muted small"
                    style={{ maxHeight: "100rem", overflowY: "auto" }}
                    dangerouslySetInnerHTML={{ __html: raffle.description }}
                  />
                </div>
              )}
            </div>

            {raffle.categories?.length ? (
              <div className="bg-white rounded-3 border p-3 mt-4">
                <span className="small text-uppercase fw-bold opacity-75">
                  Categorias
                </span>
                <div className="d-flex flex-wrap gap-2 mt-2">
                  {raffle.categories.map((raffleCategory) => {
                    const category = raffleCategory.categoryData;
                    if (!category) return null;

                    return (
                      <span
                        key={raffleCategory.id}
                        className="d-inline-flex align-items-center bg-white text-dark border rounded px-3 py-2 small fw-semibold"
                        title={category.description_short || category.name}
                      >
                        {category.name}
                      </span>
                    );
                  })}
                </div>
              </div>
            ) : null}
          </>
        )}
      </div>
    </Card>
  );
};
